import * as Complex from 'complex.js'
import {EventEmitter} from 'eventemitter3'
import TransferFunction from './TransferFunction'
import * as SVG from 'svg.js'
import 'svg.draggable.js'

enum Direction {
  ROWS,
  COLS,
}
const EPS = 1e-2
const PZ_STROKE = {width: 0.02, color: 'red'}

/**
 * Handles rendering poles and zeros from a TransferFunction. Also lets you move
 * around those poles and zeros visually.
 *
 * Emits:
 * - transferfunctionchange (tfunc: TransferFunction):
 *    poles or zeros were changed by the user, and here's the new transfer func.
 */
export default class PoleZeroPlot extends EventEmitter {
  draw: any
  xMin: number
  xMax: number
  yMin: number
  yMax: number
  xRange: number
  yRange: number
  tfunc: TransferFunction

  // Dragging poles and zeros.
  dragEl: any

  mutable: boolean

  constructor(selector: string, mutable=false) {
    super()
    this.mutable = mutable
    const el = document.querySelector(selector)
    const width = el.offsetWidth
    const height = Math.round(width * 0.75)

    this.xMin = -2.0
    this.xMax = 2.0
    this.yMin = -1.5
    this.yMax = 1.5
    this.xRange = this.xMax - this.xMin
    this.yRange = this.yMax - this.yMin

    this.draw = SVG(el).size(width, height)
    this.xRange = this.xMax - this.xMin
    this.draw.viewbox({x: this.xMin, y: this.yMin, width: this.xRange, height: this.yRange})

    this.createComplexPlot()
  }

  setTransferFunction(tfunc: TransferFunction) {
    this.tfunc = tfunc
    this.removePolesZeros()
    this.createPolesZeros()
  }

  addPole() {
    const pole = new Complex(0.7, 0.7)
    // Add both the pole and its complex conjugate.
    this.createPole(pole)
    this.createPole(pole.conjugate())
  }

  addZero() {
    const zero = new Complex(-0.7, 0.7)
    // Add both the zero and its complex conjugate.
    this.createZero(zero)
    this.createZero(zero.conjugate())
  }

  /**
   * Render the pole-zero plot SVG or something.
   */
  private createComplexPlot() {
    // Render (in SVG) a complex plane with labeled axes, and a faint grid.
    this.createGrid()
    // Draw axes.
    this.createAxes()
    // Draw a unit circle.
    this.createUnitCircle()
  }

  private createGrid() {
    this.createGridHelper(Direction.ROWS)
    this.createGridHelper(Direction.COLS)
  }

  private createGridHelper(direction: Direction) {
    const gridSpacing = 0.1
    const range = (direction == Direction.COLS) ? this.xRange : this.yRange
    const lineCount = range / gridSpacing
    for (let i = 0; i < lineCount; i++) {
      let x0, y0, x1, y1;
      if (direction == Direction.ROWS) {
        x0 = this.xMin
        y0 = this.yMin + i * gridSpacing
        x1 = this.xMax
        y1 = y0
      } else {
        x0 = this.xMin + i * gridSpacing
        y0 = this.yMin
        x1 = x0
        y1 = this.yMax
      }
      const line = this.draw.line(x0, y0, x1, y1).stroke({width: 0.001})
    }
  }

  private createAxes() {
    this.draw.line(this.xMin, 0, this.xMax, 0).stroke({width: 0.01})
    this.draw.line(0, this.yMin, 0, this.yMax).stroke({width: 0.01})
  }

  private createUnitCircle() {
    this.draw.circle(2).move(-1, -1).fill('transparent').stroke({width: 0.005})
  }

  private removePolesZeros() {
    for (let className of ['pole', 'zero']) {
      for (let poleZero of SVG.select(`g.${className}`).members) {
        poleZero.remove()
      }
    }
  }

  private createPolesZeros() {
    // Draw poles and zeros that can be moved around near the unit circle.
    for (let pole of this.tfunc.getPoles()) {
      this.createPole(pole)
    }
    for (let zero of this.tfunc.getZeros()) {
      this.createZero(zero)
    }
  }

  private createPole(point: Complex) {
    const r = 0.05
    let pole = this.draw.group()
    pole.line(-r, -r, r, r).stroke(PZ_STROKE)
    pole.line(-r, r, r, -r).stroke(PZ_STROKE)
    let hiddenStroke = Object.assign({}, PZ_STROKE)
    hiddenStroke.color = 'transparent'
    pole.circle(2*r).move(-r, -r).fill('transparent').stroke(hiddenStroke)
    pole.move(point.re, point.im)
    pole.addClass('pole')

    if (this.mutable) {
      this.makeDraggable(pole, point)
    }
    this.assignConjugateInfo(pole, point)
  }

  private createZero(point: Complex) {
    const r = 0.05
    let zero = this.draw.group()
    zero.circle(2*r).move(-r, -r).fill('transparent').stroke(PZ_STROKE)
    zero.move(point.re, point.im)
    zero.addClass('zero')

    if (this.mutable) {
      this.makeDraggable(zero, point)
    }
    this.assignConjugateInfo(zero, point)
  }

  private makeDraggable(svgEl, point: Complex) {
    svgEl.draggable()
    svgEl.on('dragend', this.onDragEnd.bind(this))
    svgEl.on('dragmove', this.onDragMove.bind(this))
    svgEl.style('cursor', 'pointer')
  }

  private assignConjugateInfo(svgEl, point) {
    // Also associate complex and conjugate value with the cell.
    svgEl.data('value', point)
    svgEl.data('conjugate', point.conjugate())
  }

  private onDragEnd(e) {
    //console.log('onDragEnd', e.detail.p.x, e.detail.p.y)

    let el = SVG.adopt(e.target)
    const oldValue = el.data('value')
    const newValue = new Complex(el.x(), el.y())

    if (newValue.abs() > 1.5) {
      // If dragged too far from the unit circle, remove it.
      el.remove()
      const pz = this.findConjugateForElement(el)
      if (pz) {
        pz.remove()
      }
    } else if (Math.abs(oldValue.im) < EPS && Math.abs(newValue.im) > EPS) {
      // If a pole or zero became imaginary, create the complex conjugate too.
      console.log('Moved point from real to imaginary. Creating a conjugate too.')
      // Create complex conjugate.
      const className = el.classes()[0]
      if (className == 'pole') {
        this.createPole(newValue.conjugate())
      } else if (className == 'zero') {
        this.createZero(newValue.conjugate())
      }
    } else if (Math.abs(oldValue.im) > EPS && Math.abs(newValue.im) < EPS) {
      // If a pole or zero became real, remove the complex conjugate.
      console.log('Moved point from imaginary to real. Finding and removing conjugate.')
      const pz = this.findConjugateForElement(el)
      pz.remove()
    }

    // Update the transfer function with new poles and zeros.
    let poles = this.getCurrentPoles()
    let zeros = this.getCurrentZeros()
    let tfunc = new TransferFunction(poles, zeros)
    this.emit('transferfunctionchange', tfunc)
  }

  private onDragMove(e) {
    //console.log('onDragMove', e.detail.p.x, e.detail.p.y)
    // Get the actual position of the new element in svg space.
    let svgEl = SVG.adopt(e.target)

    // Find the conjugate pole element and move it too.
    const conjEl = this.findConjugateForElement(svgEl)
    if (conjEl) {
      conjEl.x(svgEl.x())
      conjEl.y(-svgEl.y())
    } else {
      console.warn('Failed to find conjugate.');
    }
  }

  private onDoubleClick(e) {
    console.log('onDoubleClick')
  }

  private getCurrentPoles() {
    const elements = SVG.select(`g.pole`)
    return elements.members.map(svg => new Complex(svg.x(), svg.y()))
  }

  private getCurrentZeros() {
    const elements = SVG.select(`g.zero`)
    return elements.members.map(svg => new Complex(svg.x(), svg.y()))
  }

  private findConjugateForElement(svgEl) {
    const className = svgEl.classes()[0]
    const conjugate = new Complex(svgEl.data('value'))
    return this.findConjugate(className, conjugate)
  }

  private findConjugate(className, value) {
    const conjugate = value.conjugate()
    const elements = SVG.select(`g.${className}`)
    for (let el of elements.members) {
      const value = new Complex(el.data('value'))
      if (roughlyEquals(value, conjugate)) {
        return el
      }
    }
    return null
  }
}

function roughlyEquals(c1: Complex, c2: Complex, e=1e-4) {
  return Math.abs(c1.re - c2.re) < e && Math.abs(c1.im - c2.im) < e
}



// WEBPACK FOOTER //
// ./src/PoleZeroPlot.ts